// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Owner decision O7 (2026-10-26) — an optional review and approval step on
 * three kinds of customs tax-administration record: a dossier, a person's
 * determination of the governing rules, and an examination's conclusion (the
 * examination step whose declared role is `conclusion`).
 *
 * "An approval step: optional review/approval by a person other than the
 * recorder, under a review grant, on dossiers, determinations and examination
 * conclusions, using the existing two-person pattern."
 *
 * ## The pattern it reuses
 *
 * The two-person refusal is the one the AI register's disclosures and
 * interventions apply (`ai-disclosure-service.ts`, `ai-intervention-service.ts`)
 * and the manufacturing closures before them: a 403 with this module's own
 * code when the person approving is the person who recorded the thing, checked
 * before any state check, and a claimed write so two approvals cannot both
 * land. The approval is bound to the record's `version` at the moment it was
 * given (the 62.2.4 version-bound approval, REUSE §43): an edit afterwards
 * does not clear or downgrade it, it is shown as given against an earlier
 * version, and a later approval replaces it.
 *
 * ## What it is not
 *
 * Optional. No record needs an approval, nothing is blocked or held without
 * one, and no clock, export or transition reads it as a condition. It is a
 * second person's statement that they reviewed the record as it stood.
 *
 * Nothing here names a framework, an instrument or an authority (R-46).
 */
/** The record families a reviewer can approve (platform vocabulary). */
export const CTA_REVIEWABLE_RECORD_TYPES = [
    'dossier',
    'rule_determination',
    'examination_conclusion',
];
export function isCtaReviewableRecordType(value) {
    return (typeof value === 'string' && CTA_REVIEWABLE_RECORD_TYPES.includes(value));
}
/** The examination step role whose step is the examination's conclusion. */
export const CTA_REVIEWABLE_STEP_ROLE = 'conclusion';
export function ctaReviewStateOf(record) {
    const approval = record.review_approval ?? null;
    if (approval === null)
        return 'not_approved';
    return approval.approved_version === record.version ? 'approved' : 'approved_earlier_version';
}
export function ctaReviewRefusalsFor(record, userId) {
    if (!userId)
        return [];
    const out = [];
    if (record.created_by === userId)
        out.push('recorder');
    if (record.updated_by === userId)
        out.push('last_editor');
    if (record.determined_by?.user_id === userId)
        out.push('determiner');
    return out;
}
/** The longest note an approval carries. */
export const CTA_REVIEW_NOTE_MAX_LENGTH = 2000;
export const CUSTOMS_TAX_REVIEW_ERROR_CODES = {
    /**
     * 403: the person approving recorded the record, made its latest change, or
     * determined it (`details.refused_as`). Checked before any state check; no
     * retry by the same person changes it.
     */
    selfApproval: 'CTA_REVIEW_SELF_APPROVAL',
    /** 409: the current version is already approved (`details.approved_by`). */
    alreadyApproved: 'CTA_REVIEW_ALREADY_APPROVED',
    /** 409: a withdrawal where no approval is recorded. */
    notApproved: 'CTA_REVIEW_NOT_APPROVED',
    /** 403: a withdrawal by someone other than the approver. */
    notApprover: 'CTA_REVIEW_NOT_APPROVER',
    /** 422: an examination step that is not the examination's conclusion. */
    notAConclusion: 'CTA_REVIEW_NOT_A_CONCLUSION',
    /** 400: a record type outside {@link CTA_REVIEWABLE_RECORD_TYPES}. */
    recordTypeUnknown: 'CTA_REVIEW_RECORD_TYPE_UNKNOWN',
};
//# sourceMappingURL=customs-tax-review.js.map